import Column from '@components/Column';
import Description from '@components/Description';
import Divider from '@components/Divider';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import useRewardPolicies from '@hooks/useRewardPolicies';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { useTranslation } from 'react-i18next';

import useWillEarnStamp from './hooks/useWillEarnStamp';

/** 결제정보 (결제수단·주문방식·금액) */
function PurchaseInfo() {
    const { t } = useTranslation('order');
    const priceUnit = usePriceUnit();
    const {
        paymentMethod, orderType, totalAmount, totalDiscount, totalPointDiscount,
        giftAmount, prepaidCardAmount, pgAmount,
    } = useCalculatedRequest();
    const formatPrice = (amount: number) => `${amount.toLocaleString()}${priceUnit}`;
    // 포인트는 따로 보여주므로 할인금액에서 뺀다
    const discountAmount = totalDiscount - totalPointDiscount;
    const paymentMethodLabel = paymentMethod === 'GIFT'
        ? t('bill.gift_method')
        : t(`paymentMethod.list.${paymentMethod}`);
    const { pointEnabled } = useRewardPolicies();
    const willEarnStamp = useWillEarnStamp();

    return (
        <Wrapper>
            <Bill fullWidth spacing={4}>
                <Text size='title' fontWeight='bold' color={colors.textPrimary}>{t('bill.title')}</Text>
                <Column fullWidth spacing={2}>
                    <AmountRow label={t('bill.paymentMethod')} value={paymentMethodLabel} />
                    <AmountRow label={t('bill.orderType')}
                        value={orderType === 'TAKE_OUT' ? t('orderType.takeOut') : t('orderType.inShop')} />
                    <AmountRow label={t('bill.productAmount')} value={formatPrice(totalAmount)} />
                    <AmountRow label={t('bill.discountAmount')}
                        value={discountAmount > 0 ? `-${formatPrice(discountAmount)}` : formatPrice(0)} />
                    {pointEnabled && (
                        <AmountRow label={t('bill.point')}
                            value={`${totalPointDiscount > 0 ? '-' : ''}${totalPointDiscount.toLocaleString()}P`} />
                    )}
                    {giftAmount > 0 && (
                        <AmountRow label={t('bill.gift')} value={`-${formatPrice(giftAmount)}`} />
                    )}
                    {prepaidCardAmount > 0 && (
                        <AmountRow label={t('bill.prepaidCard')} value={`-${formatPrice(prepaidCardAmount)}`} />
                    )}
                    <Divider isThin />
                    <Row fullWidth justify='space-between'>
                        <Text size='title' fontWeight='bold' color={colors.textPrimary}>
                            {t('bill.paymentAmount')}
                        </Text>
                        <Text size='headline' fontWeight='bold' color={colors.textPrimary}>
                            {formatPrice(pgAmount)}
                        </Text>
                    </Row>
                </Column>
                {willEarnStamp !== 0 && (
                    <WillEarn contentsClassName={willEarnClassName}>
                        {t('bill.willEarnStamp')}
                        <Text fontWeight='bold'>
                            {t('bill.willEarnStampCount', { count: willEarnStamp })}
                        </Text>
                    </WillEarn>
                )}
            </Bill>
        </Wrapper>
    );
}

const WillEarn = styled(Description)`
    padding: 12px 16px;
`;

const willEarnClassName = css`justify-content: space-between;`;

function AmountRow({ label, value }: { label: string, value: string }) {
    return (
        <Row fullWidth justify='space-between'>
            <Text color={colors.textSecondary}>{label}</Text>
            <Text color={colors.textSecondary}>{value}</Text>
        </Row>
    );
}

const Wrapper = styled.div`
    padding: var(--spacing-4);
    width: 100%;
`;

const Bill = styled(Column)`
    background-color: var(--color-background-pure);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    padding: var(--spacing-4);
`;

export default PurchaseInfo;
